/**
 * SpzPairingWorkspace — admin tool to backfill vehicle SPZ from handover protocols.
 *
 * Many new vehicles (`vozidlo`) carry an empty `spz_vozu` because the plate
 * field didn't always exist. Their sales-contract handover protocols
 * (`vozidla_smlouva_predavaci_protokol.registracni_znacka`) usually do carry
 * the plate. We join vehicle → contract → protocol and offer the admin a
 * row-by-row review:
 *
 *   Detekce — "Spustit detekci" fires `GET /v1/management/vehicles/spz/
 *             candidates`. Each unpaired vehicle renders a row with the plate
 *             proposed from its protocol, a VIN-match badge, and a checkbox.
 *             Vehicles whose protocols disagree on the plate are flagged as a
 *             conflict and get a dropdown to pick which plate to apply.
 *   Backfill — ticked rows post to `POST /v1/management/vehicles/spz/backfill`,
 *             which re-validates each plate server-side against the vehicle's
 *             own protocols before writing.
 *
 * Mirrors the DedupWorkspace shell (Toolband + explicit scan CTA + admin gate).
 */

import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatInt } from '@/lib/format';
import {
  useSpzBackfillMutation,
  useSpzCandidatesQuery,
  type SpzCandidate,
} from '@/modules/management/api';

interface SpzPairingWorkspaceProps {
  isAdmin: boolean;
}

function vehicleLabel(c: SpzCandidate): string {
  return (
    [c.znacka_nazev, c.model_nazev, c.provedeni].filter((s) => s && s.trim()).join(' ').trim() ||
    (c.vc_auta ? `č. obj. ${c.vc_auta}` : `#${c.id_vozidla}`)
  );
}

// Collapse a candidate's protocol proposals to one entry per contract (the
// protocol page is addressed by contract id), keeping the distinct plates.
function contractSources(c: SpzCandidate): { id_smlouvy: number; plates: string[] }[] {
  const byContract = new Map<number, Set<string>>();
  for (const p of c.proposals) {
    const set = byContract.get(p.id_smlouvy) ?? new Set<string>();
    if (p.spz) set.add(p.spz);
    byContract.set(p.id_smlouvy, set);
  }
  return Array.from(byContract.entries()).map(([id_smlouvy, plates]) => ({
    id_smlouvy,
    plates: Array.from(plates),
  }));
}

const BATCH_SIZE_OPTIONS = [25, 40, 100] as const;

export function SpzPairingWorkspace({ isAdmin }: SpzPairingWorkspaceProps) {
  const [scanEnabled, setScanEnabled] = useState(false);
  // id_vozidla → chosen plate (presence = approved for backfill)
  const [selected, setSelected] = useState<Map<number, string>>(new Map());
  const [batchSize, setBatchSize] = useState<number>(40);
  const [page, setPage] = useState(0);

  const candidates = useSpzCandidatesQuery(isAdmin && scanEnabled);
  const backfill = useSpzBackfillMutation();

  const rows = candidates.data?.candidates ?? [];

  // Chunk candidates into batches of `batchSize` and clamp the page in case the
  // list shrank after a backfill (applied rows drop out on refetch).
  const totalBatches = Math.max(1, Math.ceil(rows.length / batchSize));
  const safePage = Math.min(page, totalBatches - 1);
  const batchStart = safePage * batchSize;
  const batchRows = rows.slice(batchStart, batchStart + batchSize);

  // Choosing a plate (conflict dropdown) auto-selects the row; the empty
  // "— vyberte —" option deselects it, keeping the checkbox + count in sync.
  const setPlate = (id: number, spz: string) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (spz.trim() === '') next.delete(id);
      else next.set(id, spz);
      return next;
    });
  };
  const toggle = (c: SpzCandidate, on: boolean) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (on) {
        next.set(c.id_vozidla, c.conflict ? (prev.get(c.id_vozidla) ?? '') : c.suggested_spz);
      } else {
        next.delete(c.id_vozidla);
      }
      return next;
    });
  };

  // Select every non-conflict row in the current batch (conflicts need a manual
  // plate choice, so they're left for the dropdown).
  const selectAllInBatch = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const c of batchRows) {
        if (!c.conflict && c.suggested_spz) next.set(c.id_vozidla, c.suggested_spz);
      }
      return next;
    });
  };

  // Select only the VIN-confirmed, non-conflicting rows in the current batch.
  const selectSafeInBatch = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const c of batchRows) {
        if (!c.conflict && c.suggested_spz && c.proposals.some((p) => p.vin_match)) {
          next.set(c.id_vozidla, c.suggested_spz);
        }
      }
      return next;
    });
  };

  // Deselect everything in the current batch.
  const clearBatch = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const c of batchRows) next.delete(c.id_vozidla);
      return next;
    });
  };

  const batchSelectedCount = batchRows.filter((c) => selected.has(c.id_vozidla)).length;

  const readyPairs = useMemo(
    () =>
      Array.from(selected.entries())
        .filter(([, spz]) => spz.trim() !== '')
        .map(([id_vozidla, spz]) => ({ id_vozidla, spz })),
    [selected],
  );

  const apply = () => {
    if (readyPairs.length === 0) {
      toast.warn('Vyberte alespoň jedno vozidlo s vybranou SPZ.');
      return;
    }
    backfill.mutate(
      { pairs: readyPairs },
      {
        onSuccess: (result) => {
          if (result.updated > 0) {
            toast.ok(`Vyplněno ${formatInt(result.updated)} SPZ.`);
          }
          if (result.skipped.length > 0) {
            toast.warn(
              `Přeskočeno ${formatInt(result.skipped.length)}: ${result.skipped
                .map((s) => `#${s.id_vozidla} (${s.reason})`)
                .join(', ')}`,
            );
          }
          setSelected(new Map());
          candidates.refetch();
        },
        onError: (err) => {
          const msg =
            err instanceof ApiError && err.body && typeof err.body === 'object'
              ? (err.body as { error?: string }).error ?? 'Vyplnění selhalo'
              : err instanceof Error
                ? err.message
                : 'Vyplnění selhalo';
          toast.error(msg);
        },
      },
    );
  };

  if (!isAdmin) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5">
        <EmptyState
          eyebrow="Pouze pro admina"
          title="Párování SPZ je dostupné pouze administrátorům firmy."
          description="Pokud potřebujete přístup, kontaktujte správce."
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <Button
          variant="ink"
          size="sm"
          icon={<Icon name="search" size={12} />}
          onClick={() => {
            setScanEnabled(true);
            setSelected(new Map());
            candidates.refetch();
          }}
          disabled={candidates.isFetching}
        >
          {scanEnabled
            ? candidates.isFetching
              ? 'Detekuji…'
              : 'Spustit detekci znovu'
            : 'Spustit detekci'}
        </Button>
        {scanEnabled && rows.length > 0 && (
          <>
            <Button variant="secondary" size="sm" onClick={selectAllInBatch}>
              Vybrat vše v dávce
            </Button>
            <Button variant="secondary" size="sm" onClick={selectSafeInBatch}>
              Vybrat bezpečné shody
            </Button>
            {batchSelectedCount > 0 && (
              <Button variant="secondary" size="sm" onClick={clearBatch}>
                Zrušit výběr v dávce
              </Button>
            )}
          </>
        )}
        <Toolband.Spacer />
        {scanEnabled && rows.length > 0 && (
          <>
            <label className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              Dávka po
              <select
                value={batchSize}
                onChange={(e) => {
                  setBatchSize(Number(e.target.value));
                  setPage(0);
                }}
                className="border border-line bg-paper px-1.5 py-1 font-mono text-[11px] text-ink focus:border-accent focus:outline-none"
              >
                {BATCH_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-1">
              <Button
                variant="secondary"
                size="sm"
                icon={<Icon name="chevronL" size={12} />}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                aria-label="Předchozí dávka"
              />
              <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-2">
                Dávka {safePage + 1} / {totalBatches}
              </span>
              <Button
                variant="secondary"
                size="sm"
                icon={<Icon name="chevronR" size={12} />}
                onClick={() => setPage((p) => Math.min(totalBatches - 1, p + 1))}
                disabled={safePage >= totalBatches - 1}
                aria-label="Další dávka"
              />
            </div>
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              {formatInt(rows.length)} vozidel · vybráno {formatInt(readyPairs.length)}
            </span>
          </>
        )}
        {readyPairs.length > 0 && (
          <Button
            variant="primary"
            size="sm"
            icon={<Icon name="check" size={12} />}
            onClick={apply}
            disabled={backfill.isPending}
          >
            {backfill.isPending
              ? 'Vyplňuji…'
              : `Vyplnit SPZ (${formatInt(readyPairs.length)})`}
          </Button>
        )}
      </Toolband>

      <div
        data-testid="management-spz-pairing"
        className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5"
      >
        {!scanEnabled ? (
          <EmptyState
            eyebrow="Připraveno k detekci"
            title="Spusťte detekci pomocí tlačítka nahoře."
            description="Detekce najde nová vozidla firmy s prázdnou SPZ, ke kterým existuje předávací protokol s registrační značkou. Shodu VIN ověříme automaticky — neshodu označíme."
          />
        ) : candidates.isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : candidates.isError ? (
          <EmptyState
            eyebrow="Chyba"
            title="Detekce selhala."
            description={
              candidates.error instanceof Error ? candidates.error.message : 'Zkuste to znovu.'
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            eyebrow="Vše v pořádku"
            title="Žádná vozidla k doplnění."
            description="Žádné nové vozidlo bez SPZ nemá v předávacím protokolu registrační značku."
          />
        ) : (
          <CandidateTable
            rows={batchRows}
            selected={selected}
            onToggle={toggle}
            onPlateChange={setPlate}
          />
        )}
      </div>
    </div>
  );
}

interface CandidateTableProps {
  rows: SpzCandidate[];
  selected: Map<number, string>;
  onToggle: (c: SpzCandidate, on: boolean) => void;
  onPlateChange: (id: number, spz: string) => void;
}

function CandidateTable({ rows, selected, onToggle, onPlateChange }: CandidateTableProps) {
  return (
    <div className="overflow-x-auto border border-line bg-paper">
      <table className="w-full border-collapse text-[12px]">
        <thead className="bg-bg">
          <tr className="text-left font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
            <th className="w-[64px] px-3 py-2.5">Vyplnit</th>
            <th className="w-[80px] px-3 py-2.5">ID</th>
            <th className="px-3 py-2.5">Vozidlo</th>
            <th className="px-3 py-2.5">VIN vozidla</th>
            <th className="px-3 py-2.5">Navržená SPZ</th>
            <th className="px-3 py-2.5">Zdroj</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const isSelected = selected.has(c.id_vozidla);
            const chosen = selected.get(c.id_vozidla) ?? c.suggested_spz;
            const anyVinMatch = c.proposals.some((p) => p.vin_match);
            // Every distinct protocol VIN, shown next to the vehicle VIN so the
            // user can eyeball the comparison (a "VIN se liší" is often a
            // one-character typo).
            const vehVinNorm = c.vin.trim().toUpperCase();
            const protoVins = Array.from(
              new Set(c.proposals.map((p) => p.protokol_vin.trim()).filter(Boolean)),
            ).map((pv) => ({ vin: pv, match: pv.toUpperCase() === vehVinNorm }));
            return (
              <tr
                key={c.id_vozidla}
                data-testid="spz-candidate"
                data-conflict={c.conflict || undefined}
                className={
                  'border-t border-line align-top transition-colors ' +
                  (isSelected ? 'bg-[color:var(--accent-soft)]' : 'hover:bg-bg')
                }
              >
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    // Conflict rows are selected by picking a plate in the dropdown,
                    // so their checkbox is read-only (can't tick an empty choice).
                    disabled={c.conflict}
                    onChange={(e) => onToggle(c, e.target.checked)}
                    aria-label={`Vyplnit SPZ pro vozidlo ${c.id_vozidla}`}
                  />
                </td>
                <td className="px-3 py-3 font-mono text-[11.5px] tabular-nums tracking-[-0.02em] text-ink-2">
                  <Link
                    to="/vozidla/$id"
                    params={{ id: String(c.id_vozidla) }}
                    className="text-accent-text hover:underline"
                  >
                    #{c.id_vozidla}
                  </Link>
                </td>
                <td className="px-3 py-3 text-ink">
                  <div className="font-medium">{vehicleLabel(c)}</div>
                  {c.vc_auta && (
                    <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-3">
                      č. obj. {c.vc_auta}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 font-mono text-[11px] tabular-nums text-ink-2">
                  <div title="VIN vozidla">
                    {c.vin || <span className="text-ink-3">— prázdné —</span>}
                  </div>
                  {protoVins.map((pv) => (
                    <div
                      key={pv.vin}
                      className={'text-[10.5px] ' + (pv.match ? 'text-ink-3' : 'text-warn')}
                      title={
                        pv.match
                          ? 'VIN v předávacím protokolu se shoduje'
                          : 'VIN v předávacím protokolu se liší od VIN vozidla'
                      }
                    >
                      {pv.match ? '=' : '≠'} protokol: {pv.vin}
                    </div>
                  ))}
                </td>
                <td className="px-3 py-3">
                  {c.conflict ? (
                    <div className="flex flex-col gap-1">
                      <select
                        value={chosen}
                        onChange={(e) => onPlateChange(c.id_vozidla, e.target.value)}
                        className="min-w-[140px] border border-line bg-paper px-2 py-1 font-mono text-[12px] text-ink focus:border-accent focus:outline-none"
                      >
                        <option value="">— vyberte SPZ —</option>
                        {Array.from(new Set(c.proposals.map((p) => p.spz))).map((spz) => (
                          <option key={spz} value={spz}>
                            {spz}
                          </option>
                        ))}
                      </select>
                      <Badge tone="warn">Konflikt SPZ</Badge>
                    </div>
                  ) : (
                    <span className="font-mono text-[13px] font-semibold tracking-[0.04em] text-ink">
                      {c.suggested_spz}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-col gap-1">
                    {/* The protocol page is keyed by contract, so collapse
                        proposals to one link per contract — listing the plate(s)
                        that contract's protocol(s) carry. */}
                    {contractSources(c).map((s) => (
                      <Link
                        key={s.id_smlouvy}
                        to="/vozidla/smlouvy/$id/protokol"
                        params={{ id: String(s.id_smlouvy) }}
                        target="_blank"
                        rel="noreferrer"
                        title={`Otevřít předávací protokol smlouvy #${s.id_smlouvy} (SPZ ${s.plates.join(', ')})`}
                        className="inline-flex items-center gap-1 font-mono text-[10.5px] uppercase tracking-[0.06em] text-accent-text hover:underline"
                      >
                        protokol · sml. #{s.id_smlouvy}
                        <Icon name="external" size={10} />
                      </Link>
                    ))}
                    {anyVinMatch ? (
                      <Badge tone="ok">VIN souhlasí</Badge>
                    ) : c.vin.trim() === '' ? (
                      <Badge tone="neutral">VIN neznámé</Badge>
                    ) : (
                      <Badge tone="warn">VIN se liší</Badge>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
